import { filter, map, sortBy } from 'lodash'
import React, { useContext, useEffect } from 'react';
import * as contractorActions from '../contractorActions';
import * as employeeActions from '../employeeActions';
import { StoreInstance } from '../store';
import { AsyncContent, Expander, JobSearchResults, sortableComponent, Spinner } from './sharedComponents';
import { JobCostStatus } from '../models/jobCostModel';
import globalRelay from '../globalRelay';
import componentServices from '../componentServices';
import JobSearchForm from './jobSearchForm';
import router from 'Scripts/router';
import util from 'Scripts/util';
import { IOperationsItem } from 'Scripts/services/operationsHelper';
import { OperationsContext } from '../contexts/operationsProvider';
import { SiteVisitStatus } from 'Scripts/models/siteVisitModel';

const SEARCH_KEY = 'operations-jobs-search';

function TableHeader({ columns, sortKey, updateSort }: { columns: Record<string, any>, sortKey?: string, updateSort: any}) {
	return (
		<thead>
			<tr>
				{ map(columns, (def, key) => {
					return !def.isSpacer? <td key={ key } className={ key === sortKey? `{def.className} sorted`: def.className } onClick={ () => updateSort(key) }>{ def.name }</td>: <td key={ key } className={ def.className } />
				}) }
			</tr>
		</thead>
	);
}

const deliveryColumns = {
	spacer1: { isSpacer: true },
	orderDate: { name: 'Order Date', className: 'date'},
	accountingId: { name: globalRelay.config!.getString('Accounting ID'), className: 'relayNumberAccountingId' },
	client: { name: 'Client', className: 'name' },
	address: { name: 'Address', className:'address' },
	deliveryCompanyName: { name: 'Delivery By', className:'name' },
	deliveryDate: { name: 'Delivery Date', className:'deliveryDate' },
	builderJobNumber: { name: 'Builder Job No.', className: 'builderJobReference' },
	spacer2: { isSpacer: true, className:'count' },
	spacer3: { isSpacer: true, className:'count' },
	spacer4: { isSpacer: true, className: 'action' },
	spacer5: { isSpacer: true },
};

const installColumns = {
	spacer1: { isSpacer: true },
	orderDate: { name: 'Order Date', className: 'date'},
	accountingId: { name: globalRelay.config!.getString('What to Install'), className: 'relayNumberAccountingId' },
	client: { name: 'Client', className: 'name' },
	address: { name: 'Address', className:'address' },
	installer: { name: 'Installer', className:'name' },
	installStartDate: { name: 'Install Start Date', className:'installStartDate' },
	builderJobNumber: { name: 'Builder Job No.', className: 'builderJobReference' },
	spacer2: { isSpacer: true, className:'count' },
	spacer3: { isSpacer: true, className:'count' },
	spacer4: { isSpacer: true, className: 'action' },
	spacer5: { isSpacer: true },
};

const inspectColumns = {
	spacer1: { isSpacer: true },
	orderDate: { name: 'Order Date', className: 'date'},
	accountingId: { name: globalRelay.config!.getString('Accounting ID'), className: 'relayNumberAccountingId' },
	client: { name: 'Client', className: 'name' },
	address: { name: 'Address', className:'address' },
	installer: { name: 'Installer', className:'name' },
	installStartdate: { name: 'Install Start Date', className:'installStartDate' },
	builderJobNumber: { name: 'Builder Job No.', className: 'builderJobReference' },
	fieldSupervisor: { name: 'Field Supervisor', className:'fieldSupervisor' },
	spacer2: { isSpacer: true, className: 'action' },
	spacer3: { isSpacer: true },
};

interface IVisitItemGroup extends IOperationsItem { 
	visitKey: string;
	isAssigned: boolean; 
	items: IOperationsItem[];
	supervisorName?: string;
	supervisorKey?: string;
}

function DeliveryRow({ jobKey, accountingId, clientContactName, address, deliveryCompanyKey, deliveryCompanyName, deliveryDate, orderDate, builderJobNumber}: 
	IOperationsItem) {

	useEffect(() => {
		if(deliveryCompanyKey && deliveryCompanyName === '-') {
			contractorActions.fetchContractor({ key: deliveryCompanyKey });
		}
	}, [deliveryCompanyKey, deliveryCompanyName]);

	const handleClick = (e) => {
		e.preventDefault();
		componentServices.router.pushHistory(componentServices.router.pushPathSegments([jobKey, 'operations']));
	}
	
	return <tr onClick={ handleClick }>
		<td />
		<td className="date">{ orderDate? util.dateHelper.formatDate(orderDate, { format: 'MM/dd/yyyy' }): '-'}</td>
		<td className="relayNumberAccountingId">{ accountingId }</td>
		<td className="name">{ clientContactName }</td>
		<td className="address">{ address }</td>
		<td className="name">{ deliveryCompanyName }</td>
		<td className="deliveryDate">{ deliveryDate? util.dateHelper.formatDate(deliveryDate, { format: 'MM/dd/yyyy' }): '-' }</td>
		<td className="builderJobReference">{ builderJobNumber }</td>
		<td className="count"></td>
		<td className="count"></td>
		<td className='action navigate'></td>
		<td></td>
	</tr>;
}

function InstallRow({ jobKey, clientContactName, address, installerName, installerKey, installStartDate, orderDate, builderJobNumber, items }: 
	IVisitItemGroup) {

	useEffect(() => {
		if(installerKey && installerName === '-') {
			employeeActions.fetchEmployee({ key: installerKey });
		}
	}, [installerName, installerKey]);

	const handleClick = (e) => {
		e.preventDefault();
		componentServices.router.pushHistory(componentServices.router.pushPathSegments([jobKey, 'operations']));
	}

	const sortedItems = sortBy(items, 'accountingId');
	const labelMarkupRows = sortedItems.reduce((markupRows: string[], item: IOperationsItem) => {
		markupRows.push(`<strong>${item.accountingId}</strong>`);

		markupRows = markupRows.concat(sortBy(item.installSheets, 'sortIndex').map((s, i) => `${i + 1}. ${s.name}`));

		return markupRows;
	}, []);
	
	return <tr onClick={ handleClick }>
		<td />
		<td className="date">{ orderDate? util.dateHelper.formatDate(orderDate, { format: 'MM/dd/yyyy' }): '-'}</td>
		<td className="relayNumberAccountingId" dangerouslySetInnerHTML={ {__html: labelMarkupRows.join('<br>')}} />
		<td className="name">{ clientContactName }</td>
		<td className="address">{ address }</td>
		<td className="name">{ installerName }</td>
		<td className="installStartDate">{ installStartDate? util.dateHelper.formatDate(installStartDate, { format: 'MM/dd/yyyy' }): '-' }</td>
		<td className="builderJobReference">{ builderJobNumber }</td>
		<td className="count"></td>
		<td className="count"></td>
		<td className='action navigate'></td>
		<td></td>
	</tr>;
}

function InspectRow({ jobKey, accountingId, clientContactName, address, fieldSupervisorName, fieldSupervisorKey, installerName, installerKey, installStartDate, orderDate, builderJobNumber}: 
	IOperationsItem) {

	useEffect(() => {
		if(installerKey && installerName === '-') {
			employeeActions.fetchEmployee({ key: installerKey });
		}
	}, [installerName, installerKey]);

	useEffect(() => {
		if(fieldSupervisorKey && fieldSupervisorName === '-') {
			employeeActions.fetchEmployee({ key: fieldSupervisorKey });
		}
	}, [fieldSupervisorName, fieldSupervisorKey]);

	const handleClick = (e) => {
		e.preventDefault();
		componentServices.router.pushHistory(componentServices.router.pushPathSegments([jobKey, 'operations']));
	}
		
	return <tr onClick={ handleClick }>
		<td />
		<td className="date">{ orderDate? util.dateHelper.formatDate(orderDate, { format: 'MM/dd/yyyy' }): '-'}</td>
		<td className="relayNumberAccountingId">{ accountingId }</td>
		<td className="name">{ clientContactName }</td>
		<td className="address">{ address }</td>
		<td className="name">{ installerName }</td>
		<td className="installStartDate">{ installStartDate? util.dateHelper.formatDate(installStartDate, { format: 'MM/dd/yyyy' }): '-' }</td>
		<td className="builderJobReference">{ builderJobNumber }</td>
		<td className="name fieldSupervisor">{ fieldSupervisorName }</td>
		<td className='action navigate'><button onClick={e => {console.log('inspection'); e.stopPropagation(); }}>Gen Inspection</button></td>
		<td></td>
	</tr>;
}

function DeliveryBucket() {
	const { operationsItems, isFetchingItems } = useContext(OperationsContext);
	const items = filter(operationsItems, item => item.requiresDelivery && item.currentStatus === JobCostStatus.order_processed);
	const DeliveryTable = sortableComponent(({ updateSort, sortKey, list }) => {
		const rows = [];
		
		list.forEach(contract => rows.push(<DeliveryRow { ...contract }></DeliveryRow>));

		return (
			<table className="sortable interactive" cellPadding="0" cellSpacing="0">
				<TableHeader updateSort={updateSort} sortKey={sortKey} columns={ deliveryColumns} />
				<tbody>
					{ rows }
				</tbody>
			</table>
		);
	});

	return <Expander title="Delivery">
		<AsyncContent hasData={ !!items.length } isFetching={ isFetchingItems }>
			<Expander title="Unscheduled">
				<div className="inner">
					<DeliveryTable list={ items.filter(i => !i.deliveryDate) } />
				</div>
			</Expander>
			<Expander title="Scheduled">
				<div className="inner">
					<DeliveryTable list={ items.filter(i => !!i.deliveryDate) } initialSortKey="orderDate" />
				</div>
			</Expander>
		</AsyncContent>
	</Expander>
}

// need to group by visit for install and inspect bucket
function InstallBucket() {
	const { operationsItems, isFetchingItems } = useContext(OperationsContext);
	const items = filter(operationsItems, item => item.requiresInstall && [SiteVisitStatus.completed, SiteVisitStatus.canceled].indexOf(item.siteVisitStatus) === -1).map(item => {
		return {
			...item, isAssigned: item.installStartDate && item.installerKey
		};
	}).reduce((list: IVisitItemGroup[], item: IOperationsItem) => {
		let group = list.find(i => i.visitKey === item.installVisitKey);

		if(!group) {
			group = {...item, visitKey: item.installVisitKey, isAssigned: false, items: [] };
			list.push(group);
		}

		group.isAssigned = group.isAssigned || (item as any).isAssigned;
		group.items.push(item);

		return list;
	}, []);

	const InstallTable = sortableComponent(({ updateSort, sortKey, list }) => {
		const rows = [];
		
		list.forEach(group => rows.push(<InstallRow { ...group }></InstallRow>));

		return (
			<table className="sortable interactive" cellPadding="0" cellSpacing="0">
				<TableHeader updateSort={updateSort} sortKey={sortKey} columns={ installColumns} />
				<tbody>
					{ rows }
				</tbody>
			</table>
		);
	});

	return <Expander title="Install">
		<AsyncContent hasData={ !!items.length } isFetching={ isFetchingItems }>
			<Expander title="Unassigned">
				<div className="inner">
					<InstallTable list={ items.filter(i => !i.isAssigned) } />
				</div>
			</Expander>
			<Expander title="Assigned">
				<div className="inner">
					<InstallTable list={ items.filter(i => !!i.isAssigned) } initialSortKey="orderDate" />
				</div>
			</Expander>
		</AsyncContent>
	</Expander>
}

function InspectionBucket() {
	const { operationsItems, isFetchingItems } = useContext(OperationsContext);
	const items = filter(operationsItems, item => item.currentStatus === JobCostStatus.installed);
	const InspectTable = sortableComponent(({ updateSort, sortKey, list }) => {
		const rows = [];
		
		list.forEach(item => rows.push(<InspectRow { ...item }></InspectRow>));

		return (
			<table className="sortable interactive" cellPadding="0" cellSpacing="0">
				<TableHeader updateSort={updateSort} sortKey={sortKey} columns={ inspectColumns} />
				<tbody>
					{ rows }
				</tbody>
			</table>
		);
	});

	return <Expander title="WAITING FOR INSPECTION">
		<AsyncContent hasData={ !!items.length } emptyContent={ !items.length && isFetchingItems? <Spinner classNames={["dark"]} />: 'No Data'}>
			<div className="inner">
				<InspectTable list={ items } initialSortKey="orderDate" />
			</div>
		</AsyncContent>
	</Expander>
}

export default function OperationsJobs({ store }: { store: StoreInstance }) {
	const searchResults = store.getIn(['viewState', 'searchResults', SEARCH_KEY]);
	const dashboardClasses = ['dashboard operations'];

	let jobContent: React.ReactElement;

	if(searchResults) {
		dashboardClasses.push('searchResults');

		jobContent = <JobSearchResults
			list={ searchResults.list }
			parameterName={ searchResults.searchParameter.name }
			searchText= { searchResults.searchText }
			onJobClick= { (j) => router.pushHistory(router.pushPathSegments(j.key)) } />;
	}
	else {
		jobContent = <React.Fragment>
			<DeliveryBucket />
			<InstallBucket />
			<InspectionBucket />
			<section className='expander expanded'>
				<header><button tabIndex={1}><span>SERVICE <span className="subtitle"><span className="statusDot"></span>5 New Issues</span></span></button>
					<div className='icon iconExpander'><span></span><span></span></div>
					<div className='actions'></div>
				</header>
				<div className=''>
					<div className='inner'>
						<table className='sortable interactive' cellPadding='0' cellSpacing='0'>
							<thead>
								<tr>
									<td></td>
									<td className='date sorted'>Order Date</td>
									<td className='relayNumberAccountingId'>Legacy ID</td>
									<td className='name'>Client</td>
									<td className='address'>Address</td>
									<td className='name'>Installer</td>
									<td className='name'>Service Tech</td>
									<td className='count centered'>New Issues</td>
									<td className='count centered'>Order</td>
									<td className='count centered'>Schedule</td>
									<td className='action'></td>
									<td></td>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td></td>
									<td className='date'>10/22/2022</td>
									<td className='relayNumberAccountingId'>000000.00</td>
									<td className='name'>Carol Danvers</td>
									<td className='address'>624 S. Sagewood Dr</td>
									<td className='name'>N. Nickerson</td>
									<td className='name'>M. Davidson</td>
									<td className='count centered'><span className="statusDot"></span>1</td>
									<td className='count centered'>2</td>
									<td className='count centered'>1</td>
									<td className='action navigate'></td>
									<td></td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</section>
		</React.Fragment>
	}

	return <div className={ dashboardClasses.join(' ') }>
		<JobSearchForm searchKey={ SEARCH_KEY } />
		{ jobContent }
	</div>;
}
